import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { SiteHeader, SiteFooter } from '../components/SiteChrome';
import type { MsgKey } from '../i18n/en';
// @ts-ignore: allow side-effect CSS import without type declarations
import '../App.css';

/**
 * Robux order tracker.
 *
 * The Robux flow is far shorter than the coins one: no credentials, no
 * screenshots, no cooldowns. The customer creates a game pass, pastes its link,
 * and waits. Everything on this page hangs off that single step.
 *
 * robuxAmount is the game pass price the customer pays for; robuxNet is what
 * Roblox actually pays out after its 30% cut. Both come from the API — this
 * page never does the arithmetic itself, so the two numbers can't drift apart.
 */

interface RobuxStatus {
  product: 'robux';
  status: 'pending' | 'delivering' | 'completed' | 'cancelled';
  rbxStatus: string | null;
  robuxAmount: number;
  robuxNet: number;
  taxPct: number;
  gamepassId: number | null;
  gamepassUrl: string | null;
  robloxUsername: string | null;
  errorReason: string | null;
  errorMessage: string | null;
  created?: string;
  providerUnreachable?: boolean;
}

/**
 * How the page treats each known issue. The words live in i18n/en.ts under
 * rbxi.<code>.t / .d / .s so they can be translated.
 */
interface RbxIssue {
  /** We're still working on it — show as waiting, not as a failure. */
  waiting?: boolean;
  /** Customer can retry once they've fixed their side. */
  canRetry?: boolean;
  /** Our problem, not theirs — never blame the customer for these. */
  ours?: boolean;
}

const RBX_ISSUES: Record<string, RbxIssue> = {
  // ── Runtime reasons (order.error.reason) ──────────────────────
  gamepass_not_found: { waiting: true, canRetry: true },
  insufficient_customer_balance: { waiting: true, ours: true },
  unknown_error: { waiting: true, canRetry: true },

  // ── Submit-time codes ─────────────────────────────────────────
  GAMEPASS_NOT_FOUND: { canRetry: true },
  GAMEPASS_PRICE_MISMATCH: { canRetry: true },
  REGIONAL_PRICING_NOT_SUPPORTED: { canRetry: true },
  GAMEPASS_SELLER_MISMATCH: { canRetry: true },
  UNIVERSE_NOT_FOUND: { canRetry: true },
  ROBLOX_USER_NOT_FOUND: { canRetry: true },
  OUT_OF_STOCK: { waiting: true },
  ROBLOX_DAILY_LIMIT: { waiting: true },
  INSUFFICIENT_FUNDS: { waiting: true, ours: true },
  ORDER_ALREADY_EXISTS: { waiting: true, ours: true },
};

const POLL_MS = 15000;

/** Step-by-step video on creating the game pass, for customers who haven't made one. */
const RBX_PASS_VIDEO = 'https://www.youtube.com/watch?v=T67T8SjSXcc';

const fmt = (n: number) => n.toLocaleString();

interface Props { customerName: string; }

const TrackRobuxPage: React.FC<Props> = ({ customerName }) => {
  const { t, rich, richList, list, has } = useI18n();
  const [data, setData] = useState<RobuxStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Two ways in. Most people paste the link, but plenty can't get it on mobile
  // or just have the pass ID in front of them, so neither route is a dead end.
  const [entryMode, setEntryMode] = useState<'pass' | 'username'>('pass');
  const [link, setLink] = useState('');
  const [robloxUser, setRobloxUser] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitCode, setSubmitCode] = useState<string | null>(null);

  const [retrying, setRetrying] = useState(false);
  const [retryMsg, setRetryMsg] = useState<string | null>(null);

  const apiBase = `/api/orders/${encodeURIComponent(customerName)}`;
  const pollRef = useRef<number | null>(null);

  const fetchStatus = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const res = await fetch(apiBase);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setFetchError(d.error || t('msg.notFound'));
        return;
      }
      setData(await res.json());
      setFetchError(null);
    } catch {
      setFetchError(t('rbx.msg.net'));
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, [customerName]);

  // Poll only while the order is actually moving, and never while the tab is
  // hidden — a backgrounded tracker shouldn't keep hitting the provider.
  const isLive = data?.status === 'delivering';
  useEffect(() => {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
    if (!isLive) return;
    pollRef.current = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchStatus();
    }, POLL_MS);
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, [isLive, customerName]);

  const canSubmit = entryMode === 'pass' ? !!link.trim() : !!robloxUser.trim();

  /**
   * Echo back the pass ID we read from whatever was pasted.
   * Display only — worker/lib/robuxMath.js does the parse that counts, and this
   * deliberately mirrors its rules so the two never disagree in front of a
   * customer. A game link's ?id= is a place, not a pass, so it is rejected here
   * exactly as it is there.
   */
  const previewPassId = (() => {
    const raw = link.trim();
    if (!raw) return null;
    if (/^\d{4,15}$/.test(raw)) return raw;
    const path = raw.match(/roblox\.com\/(?:[a-z-]+\/)?game-?pass(?:es)?\/(\d{4,15})/i);
    if (path) return path[1];
    if (/pass/i.test(raw)) {
      const query = raw.match(/[?&]id=(\d{4,15})\b/i);
      if (query) return query[1];
    }
    return null;
  })();

  const submitPass = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitCode(null);
    try {
      // Send only the fields for the chosen route — a half-filled username
      // tagging along with a pass link would just be another thing to
      // disagree about on the server.
      const body: Record<string, string> = entryMode === 'pass'
        ? { gamepassUrl: link.trim() }
        : { robloxUsername: robloxUser.trim() };
      if (entryMode === 'username' && placeId.trim()) body.placeId = placeId.trim();

      const res = await fetch(`${apiBase}/submitPass`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(d.error || t('rbx.msg.noAccept'));
        setSubmitCode(d.code || null);
        return;
      }
      setData(d);
      setLink('');
    } catch {
      setSubmitError(t('rbx.msg.net'));
    } finally {
      setSubmitting(false);
    }
  };

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    setRetryMsg(null);
    try {
      const res = await fetch(`${apiBase}/resendPass`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setRetryMsg(d.error || t('rbx.msg.retryWait'));
        return;
      }
      setData(d);
      setRetryMsg(t('rbx.msg.retryOk'));
    } catch {
      setRetryMsg(t('rbx.msg.net'));
    } finally {
      setRetrying(false);
    }
  };

  // ── Derived state ─────────────────────────────────────────────
  const isPending   = data?.status === 'pending';
  const isCancelled = data?.status === 'cancelled';
  const isDone      = data?.status === 'completed';
  const issueKey    = submitCode || data?.errorReason || null;
  const flags       = issueKey ? RBX_ISSUES[issueKey] : undefined;
  // Flags decide how the issue is treated; the words come from the language pack.
  const issue = issueKey && flags
    ? {
        ...flags,
        title: t(`rbxi.${issueKey}.t` as MsgKey),
        desc: t(`rbxi.${issueKey}.d` as MsgKey),
        steps: has(`rbxi.${issueKey}.s`) ? list(`rbxi.${issueKey}.s` as MsgKey) : [] as string[],
      }
    : undefined;
  const hasFailed   = data?.rbxStatus === 'Error';

  // The order id IS the Eldorado order id for anything sold there, so we can
  // send the customer straight to the page where they confirm and review.
  // Orders placed on our own site use a different id shape — no link for those,
  // rather than a link that lands on an Eldorado 404.
  const eldoradoUrl = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(customerName)
    ? `https://www.eldorado.gg/order/${customerName}`
    : null;

  const getHero = () => {
    if (loading)      return { color: 'muted',   icon: 'fa-circle-notch fa-spin', label: t('hero.loading'), badge: '' };
    if (fetchError)   return { color: 'danger',  icon: 'fa-exclamation-circle',   label: t('hero.notFound'), badge: '' };
    if (!data)        return { color: 'muted',   icon: 'fa-question-circle',      label: t('hero.unknown'), badge: '' };
    if (isCancelled)  return { color: 'danger',  icon: 'fa-ban',                  label: t('hero.cancelled'), badge: t('badge.cancelled') };
    if (isDone)       return { color: 'success', icon: 'fa-check-circle',         label: t('rbx.hero.done'), badge: t('badge.complete') };
    if (isPending)    return { color: 'info',    icon: 'fa-link',                 label: t('rbx.hero.pass'), badge: t('badge.action') };
    if (hasFailed && issue && !issue.waiting)
                      return { color: 'warning', icon: 'fa-exclamation-triangle', label: issue.title, badge: t('badge.action') };
    if (hasFailed)    return { color: 'info',    icon: 'fa-circle-notch fa-spin', label: issue?.title || t('rbx.hero.retrying'), badge: t('badge.retry') };
    return { color: 'active', icon: 'fa-circle-notch fa-spin', label: t('rbx.hero.delivering'), badge: t('badge.progress') };
  };
  const hero = getHero();

  return (
    <div className="tc-page">

      <SiteHeader badge="nav.robux" />

      <div className={`tr-hero tr-hero-${hero.color}`}>
        <div className="tr-hero-inner">
          <div className="tr-hero-icon-wrap">
            <i className={`fas ${hero.icon}`}></i>
          </div>
          <div className="tr-hero-text">
            {hero.badge && <span className="tr-hero-badge">{hero.badge}</span>}
            <h1 className="tr-hero-label">{hero.label}</h1>
            <p className="tr-hero-order">
              {t('common.order', { id: customerName })}
              {data && <span className="tc-platform-badge">Roblox</span>}
            </p>
            {data && (
              <p className="tr-hero-pct">
                {t('rbx.heroLine', { gross: fmt(data.robuxAmount), net: fmt(data.robuxNet) })}
              </p>
            )}
          </div>
          <button
            className="tr-hero-refresh"
            onClick={() => fetchStatus(true)}
            disabled={refreshing || loading}
            title={t('hero.refresh')}
          >
            <i className={`fas fa-sync-alt${refreshing ? ' fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      <div className="tr-content">
        {loading && (
          <div className="tc-action-block">
            <p className="tc-action-block-desc">{t('common.loading')}</p>
          </div>
        )}

        {fetchError && !loading && (
          <div className="tc-action-block tc-action-ba">
            <div className="tc-action-block-header">
              <i className="fas fa-exclamation-circle"></i>
              <span>{t('rbx.notFoundTitle')}</span>
            </div>
            <p className="tc-action-block-desc">{fetchError}</p>
          </div>
        )}

        {/* ── Step 1: the game pass ───────────────────────────── */}
        {data && isPending && (
          <>
            <div className="tc-action-block tc-action-submit">
              <div className="tc-action-block-header">
                <i className="fas fa-ticket-alt"></i>
                <span>{t('rbx.createTitle', { gross: fmt(data.robuxAmount) })}</span>
              </div>
              <p className="tc-action-block-desc">
                {rich('rbx.taxIntro', {
                  pct: data.taxPct,
                  gross: fmt(data.robuxAmount),
                  net: fmt(data.robuxNet),
                })}
              </p>

              <ol className="tr-error-steps">
                {richList('rbx.steps', { gross: fmt(data.robuxAmount) }).map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>

              {/* Rules from the Eldorado offer. A pass that breaks one is the
                  commonest reason an order stalls, so say so before it is sent. */}
              <div className="tc-tax-note">
                <i className="fas fa-clipboard-check"></i>
                <div>
                  <span className="tc-tax-note-title">{t('rbx.reqTitle')}</span>
                  <ul className="tr-error-steps" style={{ marginTop: 6 }}>
                    {richList('rbx.req').map((item, i) => (
                      <li key={i} className="tr-error-step">{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <a
                className="tc-btn tc-btn-ghost"
                href={RBX_PASS_VIDEO}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-youtube"></i>
                {t('rbx.videoBtn')}
              </a>

              <div className="tc-form">
                <div className="rbx-tabs-mini">
                  <button
                    type="button"
                    className={`rbx-tab-mini${entryMode === 'pass' ? ' rbx-tab-mini-active' : ''}`}
                    onClick={() => { setEntryMode('pass'); setSubmitError(null); setSubmitCode(null); }}
                  >
                    {t('rbx.tabPass')}
                  </button>
                  <button
                    type="button"
                    className={`rbx-tab-mini${entryMode === 'username' ? ' rbx-tab-mini-active' : ''}`}
                    onClick={() => { setEntryMode('username'); setSubmitError(null); setSubmitCode(null); }}
                  >
                    {t('rbx.tabUser')}
                  </button>
                </div>

                {entryMode === 'pass' ? (
                  <div className="tc-field">
                    <label className="tc-label">{t('rbx.passLabel')}</label>
                    <input
                      className="tc-input"
                      value={link}
                      onChange={e => setLink(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') submitPass(); }}
                      placeholder={t('rbx.passPh')}
                      autoFocus
                      inputMode="text"
                    />
                    {/* Read back what we understood so a bad paste is caught here
                        rather than after the order is sent. The server parses the
                        raw text again — this is only a reassurance. */}
                    {link.trim() !== '' && (
                      <p className="tc-action-block-desc" style={{ marginTop: 6 }}>
                        {previewPassId
                          ? rich('rbx.reading', { id: previewPassId })
                          : t('rbx.badPaste')}
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="tc-field">
                      <label className="tc-label">{t('rbx.userLabel')}</label>
                      <input
                        className="tc-input"
                        value={robloxUser}
                        onChange={e => setRobloxUser(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') submitPass(); }}
                        placeholder={t('rbx.userPh')}
                        autoFocus
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                      <p className="tc-action-block-desc" style={{ marginTop: 6 }}>
                        {rich('rbx.userHint', { gross: fmt(data.robuxAmount) })}
                      </p>
                    </div>
                    <div className="tc-field">
                      <label className="tc-label">{t('rbx.placeLabel')} <span style={{ opacity: 0.6 }}>{t('pending.optional')}</span></label>
                      <input
                        className="tc-input"
                        value={placeId}
                        onChange={e => setPlaceId(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') submitPass(); }}
                        placeholder={t('rbx.placePh')}
                        inputMode="numeric"
                      />
                    </div>
                  </>
                )}

                {submitError && (
                  <div className="tc-alert tc-alert-danger">
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{submitError}</span>
                  </div>
                )}

                <button
                  className="tc-btn tc-btn-primary tc-btn-block"
                  onClick={submitPass}
                  disabled={!canSubmit || submitting}
                >
                  <i className={`fas ${submitting ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'}`}></i>
                  {submitting ? t('rbx.sending') : entryMode === 'pass' ? t('rbx.sendPass') : t('rbx.sendUser')}
                </button>
              </div>
            </div>

            {/* A rejected submit gets the full explanation, not just the one-liner. */}
            {issue && submitCode && (
              <div className="tc-action-block tc-action-ba">
                <div className="tc-action-block-header">
                  <i className="fas fa-circle-info"></i>
                  <span>{issue.title}</span>
                </div>
                <p className="tc-action-block-desc">{issue.desc}</p>
                {issue.steps.length > 0 && (
                  <ol className="tr-error-steps">
                    {issue.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                )}
              </div>
            )}
          </>
        )}

        {/* ── Confirmed: the pass is in and nothing is wrong ───
            Shown the moment the game pass is accepted, because that is when
            the customer stops needing to do anything and starts wondering
            where their Robux are. Hidden while an error needs their attention
            — telling someone they're all set above a "fix your pass" card
            would be worse than saying nothing. */}
        {data && !isPending && !isCancelled && !hasFailed && (
          <div className="tc-action-block tc-action-resume rbx-confirm">
            <div className="tc-action-block-header">
              <i className="fas fa-circle-check"></i>
              <span>{t('rbx.allSet')}</span>
            </div>
            <p className="tc-action-block-desc">
              {isDone
                ? rich('rbx.sentDone', {
                    net: fmt(data.robuxNet),
                    user: data.robloxUsername ? ` (${data.robloxUsername})` : '',
                  })
                : rich('rbx.sentWait', { net: fmt(data.robuxNet) })}
            </p>

            <div className="rbx-notice">
              <div className="rbx-notice-title">
                <i className="fas fa-hourglass-half"></i>
                <span>{t('rbx.holdTitle')}</span>
              </div>
              <p>{rich('rbx.holdBody1')}</p>
              <p>{t('rbx.holdBody2')}</p>
            </div>

            <div className="rbx-notice rbx-notice-ask">
              <div className="rbx-notice-title">
                <i className="fas fa-star"></i>
                <span>{t('rbx.favourTitle')}</span>
              </div>
              <p>{rich('rbx.favourBody')}</p>
              {eldoradoUrl && (
                <a
                  className="tc-btn tc-btn-primary tc-btn-block"
                  href={eldoradoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-arrow-up-right-from-square"></i>
                  {t('rbx.eldoradoBtn')}
                </a>
              )}
            </div>

            {data.gamepassUrl && (
              <p className="tc-action-block-desc">
                {t('rbx.passOnFile')}{' '}
                <a href={data.gamepassUrl} target="_blank" rel="noopener noreferrer">
                  {data.gamepassUrl}
                </a>
              </p>
            )}

            {data.providerUnreachable && (
              <p className="tc-action-block-desc">{t('rbx.providerDown')}</p>
            )}
          </div>
        )}

        {/* ── Something needs attention ───────────────────────
            Only rendered when there is an actual problem. A healthy in-flight
            order is fully covered by the confirmation card above, and a second
            "your Robux are on the way" panel under it just reads as noise. */}
        {data && !isPending && !isDone && !isCancelled && issue && (
          <div className="tc-action-block tc-action-ba">
            <div className="tc-action-block-header">
              <i className="fas fa-circle-info"></i>
              <span>{issue.title}</span>
            </div>
            <p className="tc-action-block-desc">{issue.desc}</p>

            {issue.steps.length > 0 && (
              <ol className="tr-error-steps">
                {issue.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            )}

            {data.gamepassUrl && (
              <p className="tc-action-block-desc">
                {t('rbx.passLabelShort')}{' '}
                <a href={data.gamepassUrl} target="_blank" rel="noopener noreferrer">
                  {data.gamepassUrl}
                </a>
              </p>
            )}

            {retryMsg && (
              <div className="tc-alert tc-alert-info">
                <i className="fas fa-circle-info"></i>
                <span>{retryMsg}</span>
              </div>
            )}

            {issue.canRetry && (
              <button
                className="tc-btn tc-btn-primary tc-btn-block"
                onClick={retry}
                disabled={retrying}
              >
                <i className={`fas ${retrying ? 'fa-circle-notch fa-spin' : 'fa-rotate-right'}`}></i>
                {retrying ? t('rbx.retrying') : t('rbx.tryAgain')}
              </button>
            )}
          </div>
        )}


        {data && isCancelled && (
          <div className="tc-action-block tc-action-ba">
            <div className="tc-action-block-header">
              <i className="fas fa-ban"></i>
              <span>{t('rbx.cancelledTitle')}</span>
            </div>
            <p className="tc-action-block-desc">{t('rbx.cancelledBody')}</p>
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
};

export default TrackRobuxPage;
